import { Form, FormProvider } from 'react-hook-form';
import { Box, Button } from '@mui/material';
import { useEditAdditionalInformation } from './hooks';
import { t } from 'i18next';
import { TextFormField } from 'components/DynamicFormFields';
import { CustomModal, SaveChangesModal } from '@components';
import { theme } from '@store';

interface IEditAdditionalInformationProps {
  patient_id: string;
  disabled: boolean;
}

export const EditAdditionalInformation = ({
  patient_id,
  disabled = false,
}: IEditAdditionalInformationProps) => {
  const {
    formMethods,
    handleClose,
    handleOnClick,
    isOpen,
    isSubmitting,
    onSubmit,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isValid,
    isEditSubmitting,
  } = useEditAdditionalInformation(patient_id);

  const { control } = formMethods;

  return (
    <>
      <Button disabled={disabled} onClick={handleOnClick}>
        EDIT
      </Button>
      <CustomModal
        isOpen={isOpen && !disabled}
        onClose={handleClose}
        title={t('patient.summary.editAdditionalInfo.title')}
        isLoading={isSubmitting}
      >
        <FormProvider {...formMethods}>
          <Form
            control={control}
            onSubmit={e => onSubmit(e.event)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              height: 'fit-content',
              borderRadius: 0,
              backgroundColor: 'white',
              position: 'relative',
              gap: '1rem',
            }}
          >
            <TextFormField
              name='canvas_id'
              label={'Brigade EMR ID'}
              disabled={true}
            />
            <TextFormField name='emr_id' label={'EMR ID'} disabled={true} />
            <TextFormField
              name='billing_id'
              label={'Billing ID (optional)'}
              optional={true}
            />
            <TextFormField
              name='salesforce_id'
              label={'Salesforce ID'}
              disabled={true}
            />

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'row',
                gap: '0.5rem',
              }}
            >
              <TextFormField
                name='care_connect_id'
                label={'Care Connect ID'}
                disabled={true}
              />
            </Box>
            <TextFormField
              name='cms_iso_id'
              label={'CMS ISO ID (optional)'}
              optional={true}
            />
            <TextFormField
              name='customer_id'
              label={'Customer ID (optional)'}
              optional={true}
            />

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
                position: 'sticky',
                bottom: 0,
                paddingY: 3,
                backgroundColor: theme.palette.common.white,
                zIndex: 3,
              }}
            >
              <Button
                variant='text'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                }}
                disabled={isSubmitting}
                onClick={handleClose}
              >
                {t('general.cancel')}
              </Button>
              <Button
                type='submit'
                variant='contained'
                color='secondary'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                  color: 'white',
                }}
                disabled={isSubmitting || isEditSubmitting || !isValid}
                loading={isEditSubmitting}
                loadingPosition='start'
              >
                {t(`general.${isEditSubmitting ? 'saving' : 'save'}`)}
              </Button>
            </Box>
            <SaveChangesModal
              isOpen={isSaveChangesModalOpen}
              onClose={handleCloseSaveChangesModal}
              isValid={isValid}
              handleSaveChanges={handleSaveChanges}
              discardChanges={handleDiscardChanges}
              isLoading={isEditSubmitting}
            />
          </Form>
        </FormProvider>
      </CustomModal>
    </>
  );
};
